import React, { useState, useEffect } from 'react';
import './QdrantManager.css';

const QdrantManager = () => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState(0);
  const [statistics, setStatistics] = useState({
    totalVectors: 0,
    collectionSize: 0,
    lastUpdated: null,
    indexStatus: 'unknown'
  });
  const [settings, setSettings] = useState({
    qdrantUrl: 'http://localhost:6333',
    collectionName: 'well_data',
    vectorSize: 384,
    distance: 'Cosine',
    batchSize: 100
  });
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    checkConnection();
    loadStatistics();
  }, []);

  const addLog = (level, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, level, message }].slice(-50)); // Keep last 50 logs
  };

  const checkConnection = async () => {
    try {
      addLog('info', 'Checking Qdrant connection...');
      const response = await fetch('/api/qdrant/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data.connected ? 'connected' : 'disconnected');
        if (data.connected) {
          addLog('success', 'Connected to Qdrant successfully');
          setSettings(prev => ({ ...prev, ...data.settings }));
        } else {
          addLog('warning', 'Qdrant server not reachable');
        }
      } else {
        setConnectionStatus('disconnected');
        addLog('error', 'Failed to check Qdrant status');
      }
    } catch (err) {
      setConnectionStatus('disconnected');
      addLog('error', `Connection error: ${err.message}`);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/qdrant/statistics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
        addLog('info', `Loaded statistics: ${data.totalVectors} vectors`);
      }
    } catch (err) {
      addLog('warning', `Failed to load statistics: ${err.message}`);
    }
  };

  const buildVectorDatabase = async () => {
    setIsBuilding(true);
    setBuildProgress(0);
    setError('');
    setSuccess('');
    
    try {
      addLog('info', 'Starting vector database build...');
      
      const response = await fetch('/api/qdrant/build', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error(`Build failed: ${response.status}`);
      }

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setBuildProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 10;
        });
      }, 1000);

      const data = await response.json();
      
      clearInterval(progressInterval);
      setBuildProgress(100);
      
      if (data.success) {
        setSuccess('Vector database built successfully!');
        addLog('success', `Build completed: ${data.vectorsCreated} vectors created`);
        await loadStatistics();
        await checkConnection();
      } else {
        throw new Error(data.error || 'Build failed');
      }
    } catch (err) {
      setError(`Build failed: ${err.message}`);
      addLog('error', `Build failed: ${err.message}`);
    } finally {
      setIsBuilding(false);
      setTimeout(() => setBuildProgress(0), 2000);
    }
  };

  const deleteCollection = async () => {
    if (!window.confirm('Are you sure you want to delete the vector collection? This action cannot be undone.')) {
      return;
    }

    try {
      addLog('warning', 'Deleting vector collection...');
      
      const response = await fetch('/api/qdrant/delete', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setSuccess('Vector collection deleted successfully!');
        addLog('success', 'Collection deleted');
        await loadStatistics();
      } else {
        throw new Error('Failed to delete collection');
      }
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
      addLog('error', `Delete failed: ${err.message}`);
    }
  };

  const testConnection = async () => {
    addLog('info', 'Testing connection...');
    await checkConnection();
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSettings = async () => {
    try {
      const response = await fetch('/api/qdrant/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setSuccess('Settings saved successfully!');
        addLog('success', 'Settings updated');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (err) {
      setError(`Save failed: ${err.message}`);
      addLog('error', `Save failed: ${err.message}`);
    }
  };

  return (
    <div className="qdrant-manager-container">
      <div className="qdrant-manager-header">
        <h1 className="qdrant-manager-title">Qdrant Vector Database Manager</h1>
        <div className={`status-badge ${connectionStatus}`}>
          {connectionStatus === 'connected' ? 'Connected' : 
           connectionStatus === 'building' ? 'Building...' : 'Disconnected'}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="qdrant-sections">
        {/* Connection Section */}
        <div className="qdrant-section">
          <h2 className="section-title connection">Connection Status</h2>
          <div className="connection-info">
            <div className="info-row">
              <span className="info-label">Server URL:</span>
              <span className="info-value">{settings.qdrantUrl}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Collection:</span>
              <span className="info-value">{settings.collectionName}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className="info-value">{connectionStatus}</span>
            </div>
          </div>
          <div className="action-buttons">
            <button 
              className="action-button secondary"
              onClick={testConnection}
              disabled={isBuilding}
            >
              🔄 Test Connection
            </button>
          </div>
        </div>

        {/* Database Building Section */}
        <div className="qdrant-section">
          <h2 className="section-title database">Database Management</h2>
          <div className="action-buttons">
            <button 
              className="action-button primary"
              onClick={buildVectorDatabase}
              disabled={isBuilding || connectionStatus !== 'connected'}
            >
              {isBuilding ? '🔄 Building...' : '🏗️ Build Database'}
            </button>
            <button 
              className="action-button danger"
              onClick={deleteCollection}
              disabled={isBuilding || statistics.totalVectors === 0}
            >
              🗑️ Delete Collection
            </button>
          </div>
          
          {isBuilding && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${buildProgress}%` }}
                />
              </div>
              <div className="progress-text">
                Building vector database... {Math.round(buildProgress)}%
              </div>
            </div>
          )}
        </div>

        {/* Statistics Section */}
        <div className="qdrant-section">
          <h2 className="section-title statistics">Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{statistics.totalVectors.toLocaleString()}</div>
              <div className="stat-label">Total Vectors</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{(statistics.collectionSize / (1024 * 1024)).toFixed(1)}MB</div>
              <div className="stat-label">Collection Size</div>
            </div>
          </div>
          <div className="info-row">
            <span className="info-label">Last Updated:</span>
            <span className="info-value">
              {statistics.lastUpdated ? 
                new Date(statistics.lastUpdated).toLocaleString() : 
                'Never'
              }
            </span>
          </div>
        </div>

        {/* Settings Section */}
        <div className="qdrant-section">
          <h2 className="section-title settings">Settings</h2>
          <div className="settings-form">
            <div className="form-group">
              <label className="form-label">Qdrant Server URL</label>
              <input
                type="text"
                className="form-input"
                value={settings.qdrantUrl}
                onChange={(e) => handleSettingChange('qdrantUrl', e.target.value)}
                placeholder="http://localhost:6333"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Collection Name</label>
              <input
                type="text"
                className="form-input"
                value={settings.collectionName}
                onChange={(e) => handleSettingChange('collectionName', e.target.value)}
                placeholder="well_data"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Vector Size</label>
              <input
                type="number"
                className="form-input"
                value={settings.vectorSize}
                onChange={(e) => handleSettingChange('vectorSize', parseInt(e.target.value))}
                placeholder="384"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Distance Metric</label>
              <select
                className="form-input"
                value={settings.distance}
                onChange={(e) => handleSettingChange('distance', e.target.value)}
              >
                <option value="Cosine">Cosine</option>
                <option value="Euclidean">Euclidean</option>
                <option value="Dot">Dot Product</option>
              </select>
            </div>
          </div>
          <div className="action-buttons">
            <button 
              className="action-button success"
              onClick={saveSettings}
              disabled={isBuilding}
            >
              💾 Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Logs Section */}
      {logs.length > 0 && (
        <div className="qdrant-section" style={{ gridColumn: '1 / -1', marginTop: '2rem' }}>
          <h2 className="section-title">Activity Logs</h2>
          <div className="log-container">
            {logs.map((log, index) => (
              <div key={index} className="log-entry">
                <span className="log-timestamp">{log.timestamp}</span>
                <span className={`log-level ${log.level}`}>[{log.level.toUpperCase()}]</span>
                <span>{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QdrantManager; 